import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useScript } from '../../../hooks/useScript';
import {
  getVisibleQuestions,
  buildFormDataUpdates,
  buildRespuestasFromFormData,
  isQuestionAnswered,
} from './scriptUtils';
import Chips    from './questions/Chips';
import QuickDate from './questions/QuickDate';
import Opciones from './questions/Opciones';
import Texto    from './questions/Texto';

// ── Icono de estado de pregunta ───────────────────────────────────────────────
const StatusDot = ({ answered, required }) => {
  if (answered) return (
    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
  if (required) return (
    <div className="w-5 h-5 rounded-full border-2 border-red-300 bg-red-50 flex items-center justify-center flex-shrink-0">
      <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
    </div>
  );
  return (
    <div className="w-5 h-5 rounded-full border-2 border-gray-200 bg-gray-50 flex-shrink-0" />
  );
};

// ── Componente de una pregunta ────────────────────────────────────────────────
const QuestionCard = ({ pregunta, answer, onAnswer, isNew }) => {
  const answered = isQuestionAnswered(pregunta, { [pregunta.id]: answer });

  const renderInput = () => {
    switch (pregunta.tipo) {
      case 'chips':
        return <Chips pregunta={pregunta} answer={answer} onAnswer={onAnswer} />;
      case 'quick-date':
        return <QuickDate pregunta={pregunta} answer={answer} onAnswer={onAnswer} />;
      case 'opciones':
        return <Opciones pregunta={pregunta} answer={answer} onAnswer={onAnswer} />;
      case 'texto':
        return <Texto pregunta={pregunta} answer={answer} onAnswer={onAnswer} />;
      default:
        return null;
    }
  };

  return (
    <div className={`rounded-2xl border-2 overflow-hidden transition-all duration-200 ${
      answered
        ? 'border-emerald-200 bg-white shadow-sm'
        : 'border-gray-200 bg-white shadow-sm'
    } ${isNew ? 'animate-fade-in' : ''}`}>

      {/* Cabecera de la pregunta */}
      <div className={`flex items-center gap-3 px-4 py-3 ${
        answered ? 'bg-emerald-50/60' : 'bg-gray-50/60'
      }`}>
        <StatusDot answered={answered} required={pregunta.obligatorio} />
        <span className="text-sm font-bold text-gray-800 flex-1 leading-tight">
          {pregunta.pregunta}
        </span>
        {!pregunta.obligatorio && (
          <span className="text-xs text-gray-400 font-medium flex-shrink-0">opcional</span>
        )}
      </div>

      {/* Input */}
      <div className="px-4 py-3">
        {renderInput()}
      </div>
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────
const ScriptRenderer = ({ formData, updateFormData }) => {
  const { script, loading, error } = useScript(formData.tipoIncidencia);
  const [respuestas, setRespuestas] = useState({});
  const [initialized, setInitialized] = useState(false);

  // Inicializar respuestas desde formData existente (restauración de borrador)
  useEffect(() => {
    if (!script || initialized) return;
    const initial = buildRespuestasFromFormData(script.preguntas, formData);
    setRespuestas(initial);
    setInitialized(true);
  }, [script, initialized]); // eslint-disable-line

  // Preguntas visibles según las respuestas actuales
  const preguntasVisibles = useMemo(
    () => getVisibleQuestions(script?.preguntas || [], respuestas),
    [script, respuestas]
  );

  const handleAnswer = useCallback((preguntaId, answerValue) => {
    setRespuestas(prev => {
      const next = { ...prev, [preguntaId]: answerValue };

      // Si cambia una pregunta con condicional, limpiar respuestas dependientes
      const pregunta = script?.preguntas?.find(p => p.id === preguntaId);
      if (pregunta?.condicional) {
        Object.entries(pregunta.condicional).forEach(([valorId, preguntaHijaId]) => {
          const selectedId = answerValue?.selectedId;
          if (selectedId !== valorId) delete next[preguntaHijaId];
        });
      }

      // Sincronizar a formData
      const updates = buildFormDataUpdates(script?.preguntas || [], next);
      updateFormData(updates);

      return next;
    });
  }, [script, updateFormData]);

  // ── Loading ──
  if (loading) {
    return (
      <div className="px-5 py-8 flex flex-col items-center gap-3 text-gray-400">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
        <p className="text-sm">Cargando script...</p>
      </div>
    );
  }

  // ── Error / fallback ──
  if (error || !script) {
    return (
      <div className="px-5 py-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
          No se pudo cargar el script. Recarga la página o contacta con soporte.
        </div>
      </div>
    );
  }

  // ── Progreso ──
  const totalReq   = preguntasVisibles.filter(p => p.obligatorio).length;
  const answeredReq = preguntasVisibles.filter(p => p.obligatorio && isQuestionAnswered(p, respuestas)).length;

  return (
    <div className="px-4 py-4 space-y-3">

      {/* Barra de progreso del script */}
      {totalReq > 0 && (
        <div className="flex items-center gap-2.5 pb-1">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-red-500 rounded-full transition-all duration-300"
              style={{ width: `${(answeredReq / totalReq) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-400 font-medium flex-shrink-0">
            {answeredReq}/{totalReq}
          </span>
        </div>
      )}

      {/* Preguntas */}
      {preguntasVisibles.map(pregunta => (
        <QuestionCard
          key={pregunta.id}
          pregunta={pregunta}
          answer={respuestas[pregunta.id]}
          onAnswer={value => handleAnswer(pregunta.id, value)}
        />
      ))}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
      `}</style>
    </div>
  );
};

export default ScriptRenderer;
